import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Search, Loader2, CheckCircle2, Upload, X, ArrowRight, ArrowLeft, Image as ImageIcon, Wrench, AlertOctagon, MapPin,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Comandă", "Produse", "Defect", "Media", "Ridicare", "Confirmare"];

export default function PublicServiceFormPage() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";

  useEffect(() => {
    if (!embed) return;
    document.documentElement.classList.remove("dark");
    document.documentElement.classList.add("embed-mode");
    document.body.classList.add("embed-mode");
    const styleId = "ieftinit-embed-overrides-svc";
    if (!document.getElementById(styleId)) {
      const s = document.createElement("style");
      s.id = styleId;
      s.textContent = `
        html.embed-mode, body.embed-mode {
          background: transparent !important;
          height: 100vh !important;
          height: 100dvh !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
        }
        body.embed-mode > #root {
          height: 100vh !important;
          height: 100dvh !important;
          overflow: hidden !important;
          display: flex !important;
          flex-direction: column !important;
        }
        body.embed-mode > #root > * {
          flex: 1 1 auto;
          min-height: 0;
        }
        html.embed-mode { color-scheme: light !important; }
        [class*="emergent-badge"], [id*="emergent-badge"],
        a[href*="emergent.com"][target="_blank"],
        a[href*="emergent.dev"][target="_blank"],
        a[href*="emergent.sh"][target="_blank"],
        a[href*="Made with Emergent"] { display: none !important; visibility: hidden !important; }
      `;
      document.head.appendChild(s);
    }
    return () => {
      document.documentElement.classList.remove("embed-mode");
      document.body.classList.remove("embed-mode");
    };
  }, [embed]);

  const [step, setStep] = useState(0);
  const [orderNumber, setOrderNumber] = useState("");
  const [identity, setIdentity] = useState("");
  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [problemTypes, setProblemTypes] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "",
    problem_type_id: "", defect_description: "", when_started: "",
    product_used: null, visual_damage: null, notes: "",
    pickup_address1: "", pickup_city: "", pickup_zip: "",
    contact_name: "", contact_phone: "", preferred_slot: "anytime",
    accepted_terms: false,
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const fileRef = useRef();
  const contentRef = useRef(null);

  // Scroll content to top on step change.
  useEffect(() => {
    contentRef.current?.scrollTo?.({ top: 0, behavior: "instant" });
    if (embed) {
      try {
        window.parent?.postMessage(
          { type: "ieftinit:scrollTop", source: "ieftinit-service" }, "*"
        );
      } catch (_) { /* noop */ }
    }
  }, [embed, step]);

  useEffect(() => {
    api.get("/public/service/problem-types").then((r) => setProblemTypes(r.data)).catch(() => {});
  }, []);

  const lookup = async (e) => {
    e?.preventDefault?.();
    if (!orderNumber.trim()) return;
    setLoadingOrder(true);
    try {
      const id = identity.trim();
      const params = {};
      if (id) {
        if (id.includes("@")) params.email = id;
        else params.phone = id;
      }
      const { data } = await api.get(`/public/order/${orderNumber.trim().replace(/^#/, "")}`, { params });
      setOrder(data);
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || `${data.customer?.first_name || ""} ${data.customer?.last_name || ""}`.trim(),
        email: f.email || data.email || (id.includes("@") ? id : ""),
        phone: f.phone || (!id.includes("@") ? id : ""),
        contact_name: f.contact_name || `${data.customer?.first_name || ""} ${data.customer?.last_name || ""}`.trim(),
        contact_phone: f.contact_phone || (!id.includes("@") ? id : ""),
      }));
      const initial = {};
      data.line_items.forEach((li) => { initial[li.id] = { qty: 0, item: li }; });
      setSelectedItems(initial);
      setStep(1);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoadingOrder(false); }
  };

  const toggleQty = (lineId, delta, max) => {
    setSelectedItems((s) => {
      const cur = s[lineId];
      const next = Math.max(0, Math.min(max, cur.qty + delta));
      return { ...s, [lineId]: { ...cur, qty: next } };
    });
  };

  const selectedCount = Object.values(selectedItems).reduce((a, b) => a + b.qty, 0);

  const upload = async (e) => {
    const fs = Array.from(e.target.files || []);
    if (!fs.length) return;
    setUploading(true);
    try {
      for (const file of fs) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/public/service/upload", fd, { headers: { "Content-Type": undefined } });
        setFiles((cur) => [...cur, data]);
      }
      toast.success(`${fs.length} fișier(e) încărcat(e)`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!form.accepted_terms) return toast.error("Acceptă termenii");
    setSubmitting(true);
    const items = Object.values(selectedItems).filter((s) => s.qty > 0).map((s) => ({
      line_item_id: s.item.id, product_id: s.item.product_id,
      title: s.item.title, variant_title: s.item.variant_title,
      quantity: s.qty, price: s.item.price, image: s.item.image,
    }));
    if (!items.length) { setSubmitting(false); return toast.error("Selectează cel puțin un produs"); }

    try {
      const { data } = await api.post("/public/service", {
        order_number: order.order_number,
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone,
        items,
        problem_type_id: form.problem_type_id,
        defect_description: form.defect_description,
        when_started: form.when_started || null,
        product_used: form.product_used,
        visual_damage: form.visual_damage,
        notes: form.notes,
        pickup: {
          address1: form.pickup_address1,
          city: form.pickup_city,
          zip: form.pickup_zip || null,
          contact_name: form.contact_name,
          contact_phone: form.contact_phone,
          preferred_slot: form.preferred_slot,
        },
        file_ids: files.map((f) => f.id),
        accepted_terms: form.accepted_terms,
      });
      setSubmitted(data);
      setStep(5);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const canProceed = () => {
    if (step === 0) return !!order;
    if (step === 1) return selectedCount > 0;
    if (step === 2) return form.problem_type_id && form.defect_description;
    if (step === 3) return true; // media optional
    if (step === 4) return form.pickup_address1 && form.pickup_city && form.pickup_zip && form.contact_name && form.contact_phone && form.accepted_terms;
    return true;
  };

  if (submitted) {
    return (
      <div className={`${embed ? "" : "min-h-screen"} bg-background text-foreground flex items-center justify-center ${embed ? "p-4" : "p-6"}`}>
        <Card className="max-w-md w-full p-10 text-center bg-card border-border" data-testid="svc-success-card">
          <div className="h-14 w-14 rounded-xl bg-emerald-500/10 mx-auto flex items-center justify-center mb-6">
            <CheckCircle2 className="h-7 w-7 text-emerald-500" />
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight mb-2">Cerere service trimisă!</h2>
          <p className="text-muted-foreground text-sm leading-relaxed mb-6">
            Mulțumim! Echipa noastră de service o va diagnostica în maxim 48h. Vei primi un email de confirmare la <strong>{form.email}</strong>.
          </p>
          <div className="bg-secondary rounded-lg p-4 mb-6">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Numărul cererii tale</div>
            <div className="font-mono text-lg font-bold">{submitted.service_number}</div>
          </div>
          <Button variant="outline" onClick={() => window.location.reload()}>Trimite altă cerere</Button>
        </Card>
      </div>
    );
  }

  return (
    <div
      className={`ieftinit-service-root flex flex-col ${embed ? "h-screen" : "min-h-screen"} ${embed ? "bg-transparent" : "bg-background"} text-foreground`}
      style={embed ? { height: "100dvh" } : undefined}
    >
      {!embed && (
        <header className="border-b border-border bg-white flex-shrink-0">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/ieftinit-logo.png" alt="IEFTINIT.RO" className="h-10 w-auto object-contain" />
              <div className="leading-tight">
                <div className="font-display font-black text-lg">IEFTINIT.RO</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold">Cerere Service · Garanție</div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Stepper — flex-shrink-0 */}
      <div className={`flex-shrink-0 max-w-3xl mx-auto w-full px-4 sm:px-6 ${embed ? "pt-3 pb-2" : "pt-6 sm:pt-10 pb-4"}`}>
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex items-center">
              <div className={`flex flex-col items-center gap-1.5 ${i <= step ? "text-foreground" : "text-muted-foreground/60"}`}>
                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                  i < step ? "bg-primary text-primary-foreground" : i === step ? "bg-foreground text-background" : "bg-secondary"
                }`}>
                  {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </div>
                <div className="text-[10px] uppercase tracking-widest font-semibold hidden sm:block">{s}</div>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>
      </div>

      {/* Scrollable content area — flex-1, scroll intern */}
      <div
        ref={contentRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div className={`max-w-3xl mx-auto px-4 sm:px-6 pb-6 ${embed ? "pt-2" : "pt-2"}`}>

        {/* STEP 0 */}
        {step === 0 && (
          <Card className="p-6 sm:p-10 bg-card border-border" data-testid="svc-step-order">
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Solicită service pentru produsul tău.</h1>
            <p className="text-muted-foreground text-sm mb-8 leading-relaxed">
              Introdu numărul comenzii și un email sau telefon pentru a-l identifica.
            </p>
            <form onSubmit={lookup} className="space-y-4">
              <div className="space-y-2">
                <Label>Număr comandă *</Label>
                <div className="relative">
                  <Input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="ex: 88195" className="h-12 text-lg pr-12" data-testid="svc-order-input" />
                  <Search className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email sau telefon</Label>
                <Input value={identity} onChange={(e) => setIdentity(e.target.value)} placeholder="ex: nume@exemplu.ro sau 0746576207" className="h-12" data-testid="svc-identity-input" />
              </div>
              <Button type="submit" className="w-full h-12" disabled={loadingOrder || !orderNumber.trim()} data-testid="svc-lookup-btn">
                {loadingOrder ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Caută comanda <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </form>
          </Card>
        )}

        {/* STEP 1 */}
        {step === 1 && order && (
          <Card className="p-6 sm:p-10 bg-card border-border" data-testid="svc-step-products">
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Care produs are nevoie de service?</h1>
            <p className="text-muted-foreground text-sm mb-6">Selectează produsele defecte din comanda <strong>{order.name}</strong>.</p>
            <div className="space-y-3">
              {order.line_items.map((li) => {
                const sel = selectedItems[li.id];
                return (
                  <div key={li.id} className="flex items-center gap-4 p-3 border border-border rounded-lg">
                    {li.image && <img src={li.image} alt={li.title} className="h-14 w-14 rounded-md object-cover bg-secondary" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{li.title}</div>
                      <div className="text-xs text-muted-foreground">{li.variant_title}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => toggleQty(li.id, -1, li.quantity)} disabled={!sel?.qty}>−</Button>
                      <div className="font-mono text-base font-semibold w-6 text-center">{sel?.qty || 0}</div>
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => toggleQty(li.id, 1, li.quantity)} disabled={sel?.qty >= li.quantity}>+</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* STEP 2 - Defect details */}
        {step === 2 && (
          <Card className="p-6 sm:p-10 bg-card border-border" data-testid="svc-step-defect">
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2 flex items-center gap-2"><AlertOctagon className="h-7 w-7 text-red-500" /> Detalii defect</h1>
            <p className="text-muted-foreground text-sm mb-6">Descrie cât mai exact problema — ne ajută să diagnosticăm mai rapid.</p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tip problemă *</Label>
                <Select value={form.problem_type_id} onValueChange={(v) => setForm({ ...form, problem_type_id: v })}>
                  <SelectTrigger className="h-11" data-testid="svc-problem-type"><SelectValue placeholder="Alege tipul problemei" /></SelectTrigger>
                  <SelectContent>
                    {problemTypes.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Descriere defect *</Label>
                <Textarea rows={4} value={form.defect_description} onChange={(e) => setForm({ ...form, defect_description: e.target.value })} placeholder="ex: La pornire face un zgomot ciudat și se oprește după 2 secunde…" data-testid="svc-defect-description" />
              </div>
              <div className="space-y-2">
                <Label>Când a apărut problema?</Label>
                <Select value={form.when_started} onValueChange={(v) => setForm({ ...form, when_started: v })}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Alege momentul" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="from_unboxing">Din momentul despachetării</SelectItem>
                    <SelectItem value="first_use">La prima utilizare</SelectItem>
                    <SelectItem value="few_days">După câteva zile</SelectItem>
                    <SelectItem value="few_weeks">După câteva săptămâni</SelectItem>
                    <SelectItem value="months_later">După câteva luni</SelectItem>
                    <SelectItem value="other">Alt moment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Produsul a fost utilizat?</Label>
                  <RadioGroup value={form.product_used === null ? "" : String(form.product_used)} onValueChange={(v) => setForm({ ...form, product_used: v === "true" })}>
                    <label className="flex items-center gap-2 cursor-pointer text-sm"><RadioGroupItem value="true" /> Da</label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm"><RadioGroupItem value="false" /> Nu</label>
                  </RadioGroup>
                </div>
                <div className="space-y-2">
                  <Label>Deteriorare vizuală?</Label>
                  <RadioGroup value={form.visual_damage === null ? "" : String(form.visual_damage)} onValueChange={(v) => setForm({ ...form, visual_damage: v === "true" })}>
                    <label className="flex items-center gap-2 cursor-pointer text-sm"><RadioGroupItem value="true" /> Da</label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm"><RadioGroupItem value="false" /> Nu</label>
                  </RadioGroup>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Observații suplimentare</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Orice alte detalii utile…" />
              </div>
            </div>
          </Card>
        )}

        {/* STEP 3 - Media */}
        {step === 3 && (
          <Card className="p-6 sm:p-10 bg-card border-border" data-testid="svc-step-media">
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Fotografii și video</h1>
            <p className="text-muted-foreground text-sm mb-6">
              <strong className="text-foreground">Recomandare:</strong> încarcă un scurt videoclip cu defectul în acțiune — accelerează enorm diagnoza.
            </p>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center bg-secondary/30">
              <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <input ref={fileRef} type="file" multiple accept="image/*,video/*" onChange={upload} className="hidden" id="svc-file-input" data-testid="svc-file-input" />
              <label htmlFor="svc-file-input" className="text-sm text-primary cursor-pointer font-medium">
                {uploading ? "Se încarcă…" : "Click pentru a selecta fișiere"}
              </label>
              <p className="text-xs text-muted-foreground mt-1">Max 100MB / fișier · imagini și video</p>
            </div>
            {files.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mt-4">
                {files.map((f) => (
                  <div key={f.id} className="relative aspect-square rounded-md overflow-hidden border border-border bg-secondary group">
                    {f.content_type?.startsWith("image/") ? (
                      <img src={`${API_BASE}/files/${f.id}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <video src={`${API_BASE}/files/${f.id}`} className="w-full h-full object-cover" muted />
                    )}
                    <button onClick={() => setFiles((fs) => fs.filter((x) => x.id !== f.id))} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-background/80 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* STEP 4 - Pickup */}
        {step === 4 && (
          <Card className="p-6 sm:p-10 bg-card border-border" data-testid="svc-step-pickup">
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2 flex items-center gap-2"><MapPin className="h-7 w-7" /> Ridicare produs</h1>
            <p className="text-muted-foreground text-sm mb-6">Curierul nostru vine să ridice produsul. Confirmă adresa și o persoană de contact.</p>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Adresă *</Label><Input value={form.pickup_address1} onChange={(e) => setForm({ ...form, pickup_address1: e.target.value })} placeholder="Str. Florilor nr. 12" className="h-11" data-testid="svc-pickup-address" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Localitate *</Label><Input value={form.pickup_city} onChange={(e) => setForm({ ...form, pickup_city: e.target.value })} placeholder="București" className="h-11" data-testid="svc-pickup-city" /></div>
                <div className="space-y-2"><Label>Cod poștal *</Label><Input value={form.pickup_zip} onChange={(e) => setForm({ ...form, pickup_zip: e.target.value })} className="h-11" data-testid="svc-pickup-zip" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Nume contact *</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="h-11" data-testid="svc-contact-name" /></div>
                <div className="space-y-2"><Label>Telefon contact *</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+40 7XX XXX XXX" className="h-11" data-testid="svc-contact-phone" /></div>
              </div>
              <div className="space-y-2">
                <Label>Interval preferat curier</Label>
                <Select value={form.preferred_slot} onValueChange={(v) => setForm({ ...form, preferred_slot: v })}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Dimineața (09:00 – 13:00)</SelectItem>
                    <SelectItem value="afternoon">După-amiaza (13:00 – 18:00)</SelectItem>
                    <SelectItem value="anytime">Oricând în program</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-start gap-3 cursor-pointer pt-2">
                <Checkbox checked={form.accepted_terms} onCheckedChange={(v) => setForm({ ...form, accepted_terms: !!v })} data-testid="svc-accept-terms" />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  Sunt de acord cu termenii serviciului post-vânzare IEFTINIT.RO. Înțeleg că diagnoza poate dura până la 5 zile lucrătoare.
                </span>
              </label>
            </div>
          </Card>
        )}
        </div>
      </div>

      {/* Action bar — flex-shrink-0, lipit jos pe iframe. */}
      {step !== 0 && (
        <div
          className="flex-shrink-0 border-t border-border bg-background"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => setStep(step - 1)}
              data-testid="svc-nav-back"
              className="h-11"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Înapoi
            </Button>
            {step === 1 && (
              <Button
                onClick={() => setStep(2)}
                disabled={!canProceed()}
                data-testid="svc-step1-next"
                className="h-11 px-6"
              >
                Continuă ({selectedCount}) <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 2 && (
              <Button
                onClick={() => setStep(3)}
                disabled={!canProceed()}
                data-testid="svc-step2-next"
                className="h-11 px-6"
              >
                Continuă <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 3 && (
              <Button
                onClick={() => setStep(4)}
                data-testid="svc-step3-next"
                className="h-11 px-6"
              >
                {files.length ? "Continuă" : "Sari peste"} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 4 && (
              <Button
                onClick={submit}
                disabled={!canProceed() || submitting}
                data-testid="svc-submit"
                className="h-11 px-6"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Trimite cererea <ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
